import classNames from 'classnames';
import { Field, useField, useFormikContext } from 'formik';

import PropTypes from 'prop-types';
import React, { useContext, useRef, useState } from 'react';
import Image from 'next/image';
import s from './InputGroup.module.scss';
import ButtonPrimary from '../ButtonPrimary/ButtonPrimary';
import { GradientContext } from '../../containers/BasePage';

const InputGroup = ({
  name = null,
  id = null,
  label = null,
  infoMessage = null,
  children = null,
  modifiers = [],
  type,
  items,
  placeholder,
  extraErrorMessage,
  autoComplete,
  isRequired = false,
  isLargeCheckbox = false,
  showActiveIcon = false,
  isWhite = false,
  ...rest
}) => {
  const textareaRef = useRef();

  return (
    <div className={s.Field}>
      {type === 'checkbox' && (
        <Checkbox
          name={name}
          items={items}
          isLargeCheckbox={isLargeCheckbox}
          showActiveIcon={showActiveIcon}
          isWhite={isWhite}
        />
      )}
      {type === 'radio' && <Radio name={name} items={items} />}
      {type === 'textarea' && (
        <TextArea
          type={type}
          name={name}
          label={label}
          autoComplete={autoComplete}
          isRequired={isRequired}
          infoMessage={infoMessage}
          isWhite={isWhite}
          {...rest}
        />
      )}
      {type === 'text' && (
        <TextField
          name={name}
          label={label}
          type={type}
          autoComplete={autoComplete}
          isRequired={isRequired}
          infoMessage={infoMessage}
          isWhite={isWhite}
          {...rest}
        />
      )}

      {type === 'email' && (
        <TextField
          name={name}
          label={label}
          type={type}
          autoComplete={autoComplete}
          isRequired={isRequired}
          infoMessage={infoMessage}
          isWhite={isWhite}
          {...rest}
        />
      )}

      {type !== 'textarea' &&
        type !== 'text' &&
        type !== 'checkbox' &&
        type !== 'radio' &&
        type !== 'email' && (
          <OtherField
            type={type}
            name={name}
            className={classNames(s.Input, {
              [s.File]: type === 'file',
            })}
            id={id}
            placeholder={placeholder}
            autoComplete={autoComplete}
            label={label}
            isRequired={isRequired}
            infoMessage={infoMessage}
            {...rest}
          />
        )}
    </div>
  );
};

const OtherField = ({
  name,
  label,
  type,
  placeholder,
  autoComplete,
  errorMessage,
  isRequired = false,
  infoMessage,
  ...rest
}) => {
  const [field, meta] = useField(name);

  return (
    <div
      className={classNames(s.InputFieldOther, s.InputField, {
        [s.HasValue]: field.value,
        [s.HasError]: meta.touched && meta.error,
      })}>
      <label
        className={classNames(s.Label, {
          [s.Required]: isRequired,
        })}
        htmlFor={name}>
        {type === 'file' && (
          <div className={s.FileIcon}>
            <Image
              width={24}
              height={24}
              src={`/img/icon-paperclip.svg`}
              alt=""
            />
          </div>
        )}
        {label}
      </label>
      <Field
        type={type}
        name={name}
        id={name}
        className={s.InputOther}
        autoComplete={autoComplete}
        aria-describedby={infoMessage ? `${name}-description` : undefined}
        {...rest}
      />
      {infoMessage && (
        <div id={`${name}-description`} className={s.InfoMessage}>
          {infoMessage}
        </div>
      )}
      {meta.touched && meta.error && (
        <div role="alert" className={s.ErrorMessage}>
          <Image width={16} height={16} src={`/img/icon-error.svg`} alt="" />
          <span className={s.ErrorText}>{meta.error}</span>
        </div>
      )}
    </div>
  );
};

const TextField = ({
  name,
  label,
  type,
  placeholder,
  autoComplete,
  errorMessage,
  isRequired = false,
  infoMessage,
  isWhite = false,
  ...rest
}) => {
  const [field, meta] = useField(name);

  return (
    <div
      className={classNames(s.InputField, {
        [s.HasValue]: field.value,
        [s.HasError]: meta.touched && meta.error,
        [s.IsWhite]: isWhite,
      })}>
      <Field
        type={type}
        name={name}
        id={name}
        className={s.Input}
        autoComplete={autoComplete}
        aria-describedby={infoMessage ? `${name}-description` : undefined}
        {...rest}
      />
      <label
        className={classNames(s.Label, {
          [s.Required]: isRequired,
        })}
        htmlFor={name}>
        {label}
      </label>
      {infoMessage && (
        <div id={`${name}-description`} className={s.InfoMessage}>
          {infoMessage}
        </div>
      )}
      {meta.touched && meta.error && (
        <div role="alert" className={s.ErrorMessage}>
          <Image width={16} height={16} src={`/img/icon-error.svg`} alt="" />
          <span className={s.ErrorText}>{meta.error}</span>
        </div>
      )}
    </div>
  );
};

const TextArea = ({
  name,
  label,
  errorMessage,
  type,
  isRequired,
  infoMessage,
  isWhite,
  ...rest
}) => {
  const [field, meta] = useField(name);
  const textareaRef = React.useRef();
  return (
    <div
      className={classNames(s.TextAreaField, {
        [s.HasValue]: field.value,
        [s.HasError]: meta.touched && meta.error,
        [s.IsWhite]: isWhite,
      })}>
      <Field
        as="textarea"
        type={type}
        name={name}
        id={name}
        innerRef={textareaRef}
        className={s.TextArea}
        aria-describedby={infoMessage ? `${name}-description` : undefined}
        onKeyUp={() => {
          textareaRef.current.style.height = 'auto';
          textareaRef.current.style.height =
            textareaRef.current.scrollHeight + 'px';
        }}
        {...rest}
      />
      <label
        className={classNames(s.Label, {
          [s.Required]: isRequired,
        })}
        htmlFor={name}>
        {label}
      </label>
      {infoMessage && (
        <div id={`${name}-description`} className={s.InfoMessage}>
          {infoMessage}
        </div>
      )}
      {meta.touched && meta.error && (
        <div role="alert" className={s.ErrorMessage}>
          <Image width={16} height={16} src={`/img/icon-error.svg`} alt="" />
          <span className={s.ErrorText}>{meta.error}</span>
        </div>
      )}
    </div>
  );
};
const Checkbox = (props) => {
  const gradient = useContext(GradientContext);
  const [field, meta] = useField(props.name);
  const { values } = useFormikContext();
  const { isLargeCheckbox, showActiveIcon, isWhite } = props;
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const items = props.items?.length
    ? props.items
    : [{ value: props.name, label: props.name, name: props.name }];
  const selectedValues = Array.isArray(values[props.name])
    ? values[props.name]
    : [];

  return (
    <div
      className={classNames(s.CheckboxGroup, {
        [s.LargeCheckbox]: isLargeCheckbox,
        [s.IsWhite]: isWhite,
      })}>
      {items.map((item, i) => {
        const isChecked = selectedValues.includes(item.value);
        const isHovered = hoveredIndex === i;

        return (
          <div
            className={s.CheckboxItem}
            key={i}
            onMouseEnter={() => setHoveredIndex(i)}
            onMouseLeave={() => setHoveredIndex(null)}>
            <Field
              type="checkbox"
              name={props.name}
              id={`${props.name}-${item.value}`}
              value={item.value}
              className={s.Checkbox}
            />
            {isLargeCheckbox ? (
              <ButtonPrimary
                size="xsmall"
                isHovered={isHovered}
                isActive={isChecked}
                materialIcon={item.icon}
                isBlackIcon={isChecked ? false : true}
                modifiers={['InlineFlex', 'NoWrap', 'Checkbox']}>
                {showActiveIcon && isChecked && (
                  <Image
                    width={16}
                    height={16}
                    src={`/img/icon-check.svg`}
                    alt=""
                  />
                )}
                <span
                  className={classNames(s.CategoryText, {
                    [s.NoIcon]: !item.icon,
                  })}>
                  {item.label}
                </span>
              </ButtonPrimary>
            ) : (
              <label
                dangerouslySetInnerHTML={{ __html: item.label }}
                className={classNames(s.CheckboxLabel, s[gradient])}
                htmlFor={`${props.name}-${item.value}`}
              />
            )}
          </div>
        );
      })}
      {meta.touched && meta.error && (
        <div role="alert" className={s.ErrorMessage}>
          <Image width={16} height={16} src={`/img/icon-error.svg`} alt="" />
          <span className={s.ErrorText}>{meta.error}</span>
        </div>
      )}
    </div>
  );
};

const Radio = (props) => {
  const [name, meta] = useField(props.name);
  const items = props.items || [{ value: props.name, label: props.name }];

  return (
    <div className={s.RadioGroup}>
      {items.map((item, i) => (
        <div
          className={classNames(s.RadioItem, {
            [s.RadioItemWithHelp]: !!item.helpText,
          })}
          key={i}>
          <div className={s.RadioRow}>
            <Field
              type="radio"
              name={props.name}
              id={`${props.name}-${item.value}`}
              value={item.value}
              className={s.Radio}
            />
            <span className={s.RadioMark} />
            <label
              className={s.RadioLabel}
              htmlFor={`${props.name}-${item.value}`}>
              {item.label}
            </label>
          </div>
          {item.helpText && (
            <div
              className={s.RadioHelpText}
              dangerouslySetInnerHTML={{ __html: item.helpText }}
            />
          )}
        </div>
      ))}
      {meta.touched && meta.error && (
        <div role="alert" className={s.ErrorMessage}>
          <Image width={16} height={16} src={`/img/icon-error.svg`} alt="" />
          <span className={s.ErrorText}>{meta.error}</span>
        </div>
      )}
    </div>
  );
};

InputGroup.propTypes = {
  error: PropTypes.object,
  touched: PropTypes.object,
  name: PropTypes.string,
  id: PropTypes.string,
  label: PropTypes.string,
  infoMessage: PropTypes.string,
  children: PropTypes.any,
  modifiers: PropTypes.array,
};

export default InputGroup;
